"use client"

import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { ChevronDownIcon } from "lucide-react"

import { cn } from "@/utils/utils"
import { styleDrivers } from "@/utils/componentStyle"

export type AccordionItem = {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
};

export type AccordionProps = {
  items: AccordionItem[];
  type?: "single" | "multiple";
  collapsible?: boolean;
  className?: string;
  defaultValue?: string | string[];
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
};

function AccordionItemComponent({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(styleDrivers.accordion.item, className)}
      {...props}
    />
  )
}

function AccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className={styleDrivers.accordion.triggerHeader}>
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          styleDrivers.accordion.trigger,
          className
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon className={styleDrivers.accordion.triggerIcon} />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={styleDrivers.accordion.content}
      {...props}
    >
      <div className={cn(styleDrivers.accordion.contentInner, className)}>{children}</div>
    </AccordionPrimitive.Content>
  )
}

function Accordion({
  items,
  type = "single",
  collapsible = true,
  className,
  defaultValue,
  value,
  onValueChange,
}: AccordionProps) {
  if (type === "single") {
    return (
      <AccordionPrimitive.Root
        data-slot="accordion"
        type="single"
        collapsible={collapsible}
        defaultValue={defaultValue as string}
        value={value as string}
        onValueChange={onValueChange as (value: string) => void}
        className={cn(styleDrivers.accordion.root, className)}
      >
        {items.map((item) => (
          <AccordionItemComponent key={item.id} value={item.id}>
            <AccordionTrigger>{item.title}</AccordionTrigger>
            <AccordionContent>{item.content}</AccordionContent>
          </AccordionItemComponent>
        ))}
      </AccordionPrimitive.Root>
    );
  }

  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      type="multiple"
      defaultValue={defaultValue as string[]}
      value={value as string[]}
      onValueChange={onValueChange as (value: string[]) => void}
      className={cn(styleDrivers.accordion.root, className)}
    >
      {items.map((item) => (
        <AccordionItemComponent key={item.id} value={item.id}>
          <AccordionTrigger>{item.title}</AccordionTrigger>
          <AccordionContent>{item.content}</AccordionContent>
        </AccordionItemComponent>
      ))}
    </AccordionPrimitive.Root>
  );
}

export { Accordion };
export default Accordion;
